import React from 'react';
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Type, Image, Layout, PaintBucket, Filter, Palette, Boxes, Layers, Text, FileText, Lock, Timer, Wand2 } from "lucide-react";
import { useToast } from "@/hooks/useToast";
import { updateCardTitle, updateCardDescription, updateCardImage } from '@/services/api';
import { COOLDOWN_CONFIG } from '@/config/cooldowns';

interface FineControlsProps {
  cardData: {
    cardId: number;
    theme: string;
    themeId: number;
    fields: any;
    language: string;
    playerName: string;
    userId: string;
    imageUrl: string;
    blueprintId?: string;
  };
  onUpdate?: (field: string, value: string) => void;
}

interface UpdateData {
  themeId: number;
  theme: string;
  fields: any;
  language: string;
  playerName: string;
  blueprintId?: string;
}

const FineControls = ({ cardData, onUpdate }: FineControlsProps) => {
  const { toast } = useToast();
  const [loadingEndpoint, setLoadingEndpoint] = React.useState<string | null>(null);
  const [cooldowns, setCooldowns] = React.useState<{ [key: string]: number }>({});

  // Function to check if button is in cooldown
  const isInCooldown = (endpoint: string) => {
    return cooldowns[endpoint] && cooldowns[endpoint] > Date.now();
  };

  // Function to get remaining cooldown time in seconds
  const getRemainingCooldown = (endpoint: string) => {
    if (!cooldowns[endpoint]) return 0;
    const remaining = Math.ceil((cooldowns[endpoint] - Date.now()) / 1000);
    return remaining > 0 ? remaining : 0;
  };

  const startCooldown = (endpoint: string) => {
    setCooldowns(prev => ({
      ...prev,
      [endpoint]: Date.now() + COOLDOWN_CONFIG[endpoint].duration
    }));
  };

  const handleUpdate = async (endpoint: string, isLocked: boolean) => {
    if (loadingEndpoint === endpoint || isInCooldown(endpoint)) {
      return;
    }

    if (isLocked) {
      toast({
        variant: "destructive",
        title: "Feature Locked",
        description: "This feature is coming soon!",
      });
      return;
    }

    setLoadingEndpoint(endpoint);

    try {
      let response;
      const updateData: UpdateData = {
        themeId: cardData.themeId,
        theme: cardData.theme,
        fields: cardData.fields,
        language: cardData.language,
        playerName: cardData.playerName,
        blueprintId: cardData.blueprintId
      };

      switch(endpoint) {
        case 'title':
          response = await updateCardTitle(cardData.cardId, updateData);
          break;
        case 'description':
          response = await updateCardDescription(cardData.cardId, updateData);
          break;
        case 'image':
          response = await updateCardImage(cardData.cardId, updateData);
          break;
        default:
          throw new Error(`Unknown endpoint: ${endpoint}`);
      }

      // console.log(`Response from ${endpoint} update:`, response);

      if (endpoint === 'image') {
        const imageUrl = response.data.data.imageUrl;
        console.log('New image URL:', imageUrl);
        if (onUpdate && imageUrl) {
          onUpdate('imageUrl', imageUrl);
        }
      } else {
        const updatedValue = response.data.data[endpoint];
        if (onUpdate && updatedValue) {
          onUpdate(endpoint, updatedValue);
        }
      }

      toast({
        title: 'Success',
        description: `Successfully updated ${endpoint}`,
      });

      // Start cooldown after successful operation
      startCooldown(endpoint);
    } catch (error) {
      console.error(`Error updating ${endpoint}:`, error);
      toast({
        title: 'Error',
        description: `Failed to update ${endpoint}`,
        variant: 'destructive',
      });
    } finally {
      setLoadingEndpoint(null);
    }
  };

  // Effect to update cooldown timers
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCooldowns(prev => {
        const now = Date.now();
        const updated = { ...prev };
        let changed = false;

        Object.keys(updated).forEach(key => {
          if (updated[key] <= now) {
            delete updated[key];
            changed = true;
          }
        });

        return changed ? updated : prev;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const buttons = [
    { 
      icon: <Type size={20} />, 
      label: "Title", 
      description: "Regenerate the card's title",
      endpoint: 'title',
      isLocked: false
    },
    { 
      icon: <FileText size={20} />, 
      label: "Description", 
      description: "Regenerate the card's description",
      endpoint: 'description',
      isLocked: false
    },
    { 
      icon: <Image size={20} />, 
      label: "Image", 
      description: "Regenerate the card's image",
      endpoint: 'image',
      isLocked: false
    },
    { 
      icon: <PaintBucket size={20} />, 
      label: "Texture", 
      description: "Apply different textures to elements",
      endpoint: 'texture',
      isLocked: true
    },
    { 
      icon: <Layout size={20} />, 
      label: "Layout", 
      description: "Change the layout and arrangement of elements",
      endpoint: 'layout',
      isLocked: true
    },
    { 
      icon: <Filter size={20} />, 
      label: "Filters", 
      description: "Apply visual filters to images and elements",
      endpoint: 'filters',
      isLocked: true
    },
    { 
      icon: <Palette size={20} />, 
      label: "Colors", 
      description: "Modify color schemes and palettes",
      endpoint: 'colors',
      isLocked: true
    },
    { 
      icon: <Boxes size={20} />, 
      label: "Elements", 
      description: "Add or modify document elements",
      endpoint: 'elements',
      isLocked: true
    },
    { 
      icon: <Layers size={20} />, 
      label: "Layers", 
      description: "Manage layers and element stacking",
      endpoint: 'layers',
      isLocked: true
    },
    { 
      icon: <Text size={20} />, 
      label: "Format", 
      description: "Format text styles and typography",
      endpoint: 'format',
      isLocked: true
    }
  ];

  return (
    <TooltipProvider delayDuration={300}>
      <div className="w-full">
        <div className="flex items-center gap-2 mb-4">
          <Wand2 className="h-6 w-6 text-purple-500" />
          <h2 className="text-xl font-semibold text-zinc-100">Fine Controls</h2>
        </div>

        <div className="grid grid-cols-2 gap-4 border border-zinc-800 rounded-lg p-6">
          {buttons.map((button, index) => {
            const cooldownRemaining = getRemainingCooldown(button.endpoint);
            const inCooldown = isInCooldown(button.endpoint);

            return (
              <Tooltip key={index}>
                <TooltipTrigger asChild>
                  <Button
                    variant="secondary"
                    className={`h-24 w-full flex flex-col items-center justify-center gap-2 bg-black hover:bg-zinc-900 border border-zinc-800 relative
                      ${(button.isLocked || loadingEndpoint || inCooldown) 
                        ? 'opacity-60 cursor-not-allowed hover:bg-background' 
                        : ''}`}
                    onClick={() => handleUpdate(button.endpoint, button.isLocked)}
                    disabled={button.isLocked || loadingEndpoint !== null || Boolean(inCooldown)}
                  >
                    {loadingEndpoint === button.endpoint ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-zinc-400" />
                    ) : (
                      React.cloneElement(button.icon, { className: "text-zinc-400" })
                    )}
                    <span className="text-sm text-zinc-300">{button.label}</span>
                    {button.isLocked && (
                      <Lock className="h-4 w-4 absolute top-2 right-2 text-zinc-500" />
                    )}
                    {!button.isLocked && loadingEndpoint && loadingEndpoint !== button.endpoint && (
                      <Timer className="h-4 w-4 absolute top-2 right-2 text-amber-500 animate-pulse" />
                    )}
                    {inCooldown && (
                      <div className="absolute top-2 left-2 flex items-center gap-1">
                        <Timer className="h-4 w-4 text-blue-400" />
                        <span className="text-xs text-blue-400">{cooldownRemaining}s</span>
                      </div>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" className="bg-black text-zinc-100 border border-zinc-800">
                  <p>{button.description}</p>
                  {inCooldown && (
                    <p className="text-xs text-blue-400 mt-1">
                      Cooldown: {cooldownRemaining}s remaining
                    </p>
                  )}
                  {!button.isLocked && loadingEndpoint && loadingEndpoint !== button.endpoint && (
                    <p className="text-xs text-amber-500 mt-1">Please wait for the current operation to complete</p>
                  )}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </div>
    </TooltipProvider>
  );
};

export default FineControls; 